import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {Route} from 'react-router-dom';
import {Panel} from 'react-bootstrap';
import {Button, ButtonBase, Grid} from '@material-ui/core';
import {AddCircleOutline as AddCircleOutlineIcon, PlayArrow as PlayArrowIcon} from '@material-ui/icons';
import {isEmpty} from 'lodash/lang';
import moment from 'moment';
import {CalendarDate, CommonModal, DeletionConfirmBox, ImageWrapper, Modal, ScrollToLoadWrapper} from '../common';
import {triggerFireworks} from 'components/common/Fireworks';
import {SecondaryNavigation} from 'components/Navigation';
import ReadingCreation from './ReadingCreation';
import ReadingUpdate from './ReadingUpdate';
import BadlyWrittenReadingThermometer from './BadlyWrittenReadingThermometer';
import ReadingForm from './ReadingForm';
import ReaderAwardList from '../Award/ReaderAwardList';
import {deleteReading, fetchReadings} from 'api/readings';
import {fetchAllCommonImages} from 'api/images';
import {fetchTotalReadingDates} from 'actions/account';
import {fetchUser, setCurrentUser} from 'actions/users';
import {setCurrentReading, clearReadings} from 'actions/readings';
import {formatErrors} from 'actions/errors';
import {addFlashMessage} from 'actions/flash-message';
import {buildMediaFileUrl, buildUserFullName, momentDatetime} from 'utils/common';
import * as permissions from 'utils/permissions';

class ReadingList extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  static propTypes = {
    readingPages: PropTypes.object.isRequired,
    onSendReadingMessageModalOpen: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      readingDetailShown: false,
      lastReading: null,
      readingsKey: 0,
      readingToRemove: null,
      groupedReadingReactions: null,
      activeReadingReactions: null,
      awardsModalShown: false,
    };
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.fetchReadingReactions = this.fetchReadingReactions.bind(this);
    this.handleSubmitSuccess = this.handleSubmitSuccess.bind(this);
    this.handleReadingSelect = this.handleReadingSelect.bind(this);
    this.closeReadingDetail = this.closeReadingDetail.bind(this);
    this.deleteReading = this.deleteReading.bind(this);
    this.showDeletionConfirmBox = this.showDeletionConfirmBox.bind(this);
  }

  componentDidMount() {
    const {isMeReader} = this.props;
    if (!isMeReader) {
      const {userId, fetchUser, formatErrors} = this.props;
      fetchUser(userId).catch((err) => formatErrors(err, '/users'));
    }
    this.fetchReadingReactions();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.totalReadingDates &&
      typeof nextProps.totalReadingDates === 'number' &&
      nextProps.totalReadingDates > this.props.totalReadingDates &&
      nextProps.totalReadingDates % 50 === 0
    ) {
      triggerFireworks();
    }
  }

  showModal(type) {
    this.setState({
      [`${type}ModalShown`]: true,
    });
  }

  closeModal() {
    this.setState({
      awardsModalShown: false,
    });
  }

  fetchReadingReactions() {
    const {
      user: {level},
    } = this.props;
    return fetchAllCommonImages({type: 'reading-reactions', level}).then(({records}) => {
      const groupedReadingReactions = {};
      const activeReadingReactions = [];
      for (const record of records) {
        const {versionCode, order, image, active} = record;
        if (!groupedReadingReactions[versionCode]) {
          groupedReadingReactions[versionCode] = {};
        }
        groupedReadingReactions[versionCode][order] = image;
        if (active) {
          activeReadingReactions.push(record);
        }
      }
      this.setState({groupedReadingReactions, activeReadingReactions});
    });
  }

  handleSubmitSuccess() {
    const {fetchTotalReadingDates} = this.props;
    this.setState((prevState) => ({
      readingsKey: prevState.readingsKey + 1,
    }));
    fetchTotalReadingDates();
  }

  handleReadingSelect(reading) {
    const {setCurrentReading, isMeReader} = this.props;
    const {
      router: {
        history: {push},
      },
    } = this.context;
    setCurrentReading(reading);
    if (isMeReader) {
      push(`/readings/${reading.id}/update`);
    } else {
      this.setState({
        readingDetailShown: true,
      });
    }
  }

  closeReadingDetail() {
    this.setState({
      readingDetailShown: false,
    });
  }

  deleteReading(reading) {
    const {addFlashMessage, formatErrors, userId} = this.props;
    deleteReading(reading.id)
      .then(() => {
        addFlashMessage('The reading has been successfully deleted!', 'success');
        this.context.router.history.push(`/users/${userId}`);
      })
      .catch((err) => formatErrors(err));
  }

  showDeletionConfirmBox(reading) {
    this.setState(
      {
        readingToRemove: reading,
      },
      () => {
        ReactDOM.findDOMNode(this.deletionConfirmBoxRef)
          .querySelector('.btn-show-confirm')
          .click();
      },
    );
  }

  render() {
    const {
      router: {
        history: {push},
      },
    } = this.context;
    const {
      readingDetailShown,
      lastReading,
      readingsKey,
      readingToRemove,
      groupedReadingReactions,
      activeReadingReactions,
      awardsModalShown,
    } = this.state;
    const {userId, isMeReader, user, totalReadingDates, onSendReadingMessageModalOpen} = this.props;
    let lastDateCreated = momentDatetime()
      .add(1, 'day')
      .startOf('day');
    return (
      <React.Fragment>
        <Modal
          onClose={() => this.closeModal()}
          open={awardsModalShown}
          maxWidth="lg"
          renderContent={() => <ReaderAwardList userId={userId} />}
        />
        <CommonModal title="Reading Detail" onClose={this.closeReadingDetail} show={readingDetailShown} backdrop>
          <ReadingForm.Update readOnly={user.classroomReadOnly} user={user} onSubmit={() => null} />
        </CommonModal>
        <DeletionConfirmBox
          ref={(ref) => (this.deletionConfirmBoxRef = ref)}
          itemName="reading"
          deleteAction={() => this.deleteReading(readingToRemove)}
          DeleteButton="span"
        />
        <div>
          {isMeReader && (
            <div>
              <SecondaryNavigation
                tabs={[
                  {url: '/readings', label: 'Journal', isActive: true},
                  {url: '/tips', label: 'Tips', isActive: false},
                  {url: 'http://blog.kluwell.com/', label: 'Resources', isActive: false},
                  {url: '/awards', label: 'Awards', isActive: false},
                ]}
              />
            </div>
          )}
          <div className="container content-box">
            <Panel>
              <h3 className="section-header">
                <div className="section-title">Reading Journal</div>
                {!isMeReader && (
                  <Button variant="contained" color="secondary" onClick={() => this.showModal('awards')}>
                    Show Student's Awards
                  </Button>
                )}
              </h3>
              {isMeReader && (
                <React.Fragment>
                  <div className="flex-row-start">
                    {user.avatar && (
                      <ImageWrapper width={100} height={100} className="avatar-wrapper">
                        <img
                          alt="Avatar"
                          src={user.avatar.startsWith('/images') ? user.avatar : buildMediaFileUrl({url: user.avatar})}
                        />
                      </ImageWrapper>
                    )}
                    <h4 className="small-margin">{buildUserFullName(user)}</h4>
                  </div>
                  <br />
                  {!user.classroomReadOnly && (
                    <div className="reading-buttons">
                      <Button variant="contained" color="secondary" onClick={() => push('/readings/create')}>
                        <AddCircleOutlineIcon />
                        New Reading
                      </Button>
                      <Button variant="contained" color="secondary" onClick={() => push('/readings/continue')}>
                        <PlayArrowIcon />
                        Continue Reading
                      </Button>
                    </div>
                  )}
                  <Route
                    path="/readings/continue"
                    render={(props) => (
                      <ReadingCreation
                        {...{
                          ...props,
                          lastReading,
                          activeReadingReactions,
                          studentSetReadingPeriod: isMeReader ? user.activeClassroom.studentSetReadingPeriod : null,
                          onSubmitSuccess: this.handleSubmitSuccess,
                        }}
                      />
                    )}
                  />
                  <Route
                    path="/readings/create"
                    render={(props) => (
                      <ReadingCreation
                        {...{
                          ...props,
                          activeReadingReactions,
                          studentSetReadingPeriod: isMeReader ? user.activeClassroom.studentSetReadingPeriod : null,
                          onSubmitSuccess: this.handleSubmitSuccess,
                        }}
                      />
                    )}
                  />
                  <Route
                    path="/readings/:readingId(\d+)"
                    render={(props) => (
                      <ReadingUpdate
                        {...{
                          ...props,
                          readOnly: user.classroomReadOnly,
                          activeReadingReactions,
                          studentSetReadingPeriod: isMeReader ? user.activeClassroom.studentSetReadingPeriod : null,
                          onSubmitSuccess: this.handleSubmitSuccess,
                        }}
                      />
                    )}
                  />
                  <BadlyWrittenReadingThermometer score={totalReadingDates} />
                </React.Fragment>
              )}
              <ScrollToLoadWrapper
                key={readingsKey}
                className="reading-scrollable"
                fetchFunc={({page, perPage}) =>
                  fetchReadings(page, perPage, userId).then((response) => {
                    const {records} = response;
                    if (page === 1 && !isEmpty(records)) {
                      this.setState({
                        lastReading: records[0],
                      });
                    }
                    return response;
                  })
                }
                list={({records}) =>
                  records.map((reading) => {
                    const dateCreated = momentDatetime(reading.dateCreated).startOf('day');
                    const grouped = lastDateCreated && lastDateCreated.isSame(dateCreated);
                    lastDateCreated = dateCreated;
                    return (
                      <div
                        key={reading.id}
                        className={classNames({
                          'reading-row': true,
                          'unset-margin': grouped,
                        })}
                      >
                        <CalendarDate
                          className={classNames({'visibility-hidden': grouped})}
                          datetime={reading.dateCreated}
                        />
                        <ButtonBase className="reading-preview" onClick={() => this.handleReadingSelect(reading)}>
                          <div>
                            <h3 style={{textAlign: 'left'}}>{reading.bookTitle}</h3>
                            {!isMeReader && reading.comment && (
                              <h4 style={{margin: '10px 0', textAlign: 'left'}}>Comment: {reading.comment}</h4>
                            )}
                            <Grid container>
                              <Grid item xs={2}>
                                <h4>VIEW</h4>
                              </Grid>
                              {onSendReadingMessageModalOpen && (
                                <Grid item xs={4}>
                                  <ButtonBase
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSendReadingMessageModalOpen({reading});
                                    }}
                                  >
                                    <h4>SEND A MESSAGE</h4>
                                  </ButtonBase>
                                </Grid>
                              )}
                              {!isMeReader && (
                                <Grid item xs={4}>
                                  <ButtonBase
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.showDeletionConfirmBox(reading);
                                    }}
                                  >
                                    <h4>DELETE READING</h4>
                                  </ButtonBase>
                                </Grid>
                              )}
                            </Grid>
                          </div>
                          {groupedReadingReactions && (
                            <img
                              src={buildMediaFileUrl({
                                url:
                                  groupedReadingReactions[reading.reactionImageVersionCode][reading.reactionImageOrder],
                              })}
                            />
                          )}
                        </ButtonBase>
                      </div>
                    );
                  })
                }
                anchor={({anchorRef, hasNextPage, isFetching, fetchMoreRecords}) => (
                  <Button
                    buttonRef={anchorRef}
                    color="secondary"
                    fullWidth
                    onClick={fetchMoreRecords}
                    disabled={!hasNextPage}
                  >
                    {hasNextPage ? (isFetching ? 'Loading...' : 'Load more readings') : 'No more readings'}
                  </Button>
                )}
              />
            </Panel>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {account, users, readings} = state;
  const userId = parseInt(ownProps.userId || ownProps.match.params.userId);
  let user = account.get('user').toJS();
  let totalReadingDates = account.get('totalReadingDates', 0);
  const isMeReader = permissions.isReader(user.role);
  if (!isMeReader) {
    user = users.get('currentUser').toJS();
    totalReadingDates = null;
  }
  return {
    userId: isMeReader ? user.id : userId,
    isMeReader,
    user,
    totalReadingDates,
    readingPages: readings.get('readingPages'),
  };
};

export default connect(mapStateToProps, {
  formatErrors,
  addFlashMessage,
  fetchUser,
  fetchTotalReadingDates,
  setCurrentReading,
  clearReadings,
})(ReadingList);
